import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Copy, Trash2, Eye, EyeOff, BookOpen, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, EmptyState } from '../layout/Primitives';
import { MenuCanvas } from './MenuCanvas';
import { firstPageElements } from './editorState';
import { useFonts } from './fonts';
import { DownloadMenuButton } from './UserMenus';
import DeleteConfirmDialog from '../DeleteConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MenusManager = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { customFonts } = useFonts();

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/menus`, auth);
      setMenus(res.data);
    } catch (e) {
      toast.error('Failed to load menus');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const createMenu = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/admin/menus`, { title: 'Untitled menu' }, auth);
      navigate(`/admin/menus/${res.data.id}`);
    } catch (e) {
      toast.error('Failed to create menu');
      setCreating(false);
    }
  };

  const toggleStatus = async (m) => {
    const status = m.status === 'published' ? 'draft' : 'published';
    try {
      const res = await axios.put(`${API}/admin/menus/${m.id}`, { status }, auth);
      setMenus((prev) => prev.map((x) => (x.id === m.id ? res.data : x)));
      toast.success(status === 'published' ? 'Menu published — members can see it now' : 'Menu unpublished');
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const duplicate = async (m) => {
    try {
      const res = await axios.post(`${API}/admin/menus/${m.id}/duplicate`, {}, auth);
      setMenus((prev) => [res.data, ...prev]);
      toast.success('Menu duplicated');
    } catch (e) {
      toast.error('Failed to duplicate');
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/menus/${toDelete.id}`, auth);
      setMenus((prev) => prev.filter((x) => x.id !== toDelete.id));
      toast.success('Menu deleted');
      setToDelete(null);
    } catch (e) {
      toast.error('Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="menus-manager">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold tracking-tight text-white sm:text-3xl">Menus</h2>
          <p className="mt-1 text-sm text-[#a1a1aa]">Design pages members see in their Menu tab.</p>
        </div>
        <Button
          data-testid="new-menu-btn"
          onClick={createMenu}
          disabled={creating}
          className="h-11 shrink-0 rounded-full bg-[#EAB308] px-5 font-semibold text-black hover:bg-[#CA8A04]"
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-2 h-4 w-4" /> New menu</>}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#A855F7]" /></div>
      ) : menus.length === 0 ? (
        <EmptyState icon={BookOpen} title="No menus yet" message="Create your first menu and design it like a poster — text, images and shapes anywhere." testId="menus-empty" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-menus-list">
          {menus.map((m) => (
            <Card key={m.id} className="overflow-hidden" data-testid={`admin-menu-card-${m.id}`}>
              <button type="button" onClick={() => navigate(`/admin/menus/${m.id}`)} className="relative block h-52 w-full overflow-hidden bg-black text-left">
                <MenuCanvas menu={m} width={300} elements={firstPageElements(m)} customFonts={customFonts} className="mx-auto" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#18181b] to-transparent" />
                <span
                  className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${
                    m.status === 'published' ? 'bg-[#10b981] text-black' : 'bg-white/[0.12] text-white'
                  }`}
                  data-testid={`menu-status-${m.id}`}
                >
                  {m.status}
                </span>
              </button>
              <div className="px-4 py-3">
                <p className="truncate font-heading text-[15px] font-semibold text-white">{m.title}</p>
                <p className="text-xs text-[#71717a]">
                  {m.pages?.length || 1} page{(m.pages?.length || 1) === 1 ? '' : 's'} · updated {new Date(m.updated_at).toLocaleDateString()}
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <Button
                    data-testid={`edit-menu-btn-${m.id}`}
                    onClick={() => navigate(`/admin/menus/${m.id}`)}
                    size="sm"
                    className="h-9 flex-1 rounded-full bg-[#A855F7] text-white hover:bg-[#9333EA]"
                  >
                    <Pencil className="mr-1.5 h-4 w-4" /> Edit
                  </Button>
                  <Button
                    data-testid={`toggle-menu-status-btn-${m.id}`}
                    onClick={() => toggleStatus(m)}
                    variant="outline"
                    size="sm"
                    className={`h-9 w-9 rounded-full p-0 ${m.status === 'published' ? 'border-[#10b981]/50 text-[#10b981]' : 'border-white/[0.12] text-[#a1a1aa]'} hover:text-white`}
                    title={m.status === 'published' ? 'Unpublish' : 'Publish'}
                  >
                    {m.status === 'published' ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </Button>
                  <DownloadMenuButton menu={m} customFonts={customFonts} testId={`download-menu-btn-${m.id}`} className="h-9 w-9 border border-white/[0.12] !bg-transparent text-[#a1a1aa] hover:text-white" />
                  <Button data-testid={`duplicate-menu-btn-${m.id}`} onClick={() => duplicate(m)} variant="outline" size="sm" className="h-9 w-9 rounded-full border-white/[0.12] p-0 text-[#a1a1aa] hover:text-white" title="Duplicate">
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button data-testid={`delete-menu-btn-${m.id}`} onClick={() => setToDelete(m)} variant="outline" size="sm" className="h-9 w-9 rounded-full border-[#f43f5e]/50 p-0 text-[#f43f5e] hover:bg-[#f43f5e] hover:text-white" title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <DeleteConfirmDialog
        isOpen={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={remove}
        loading={deleting}
        title="Delete menu?"
        message={`"${toDelete?.title}" will be removed for members too. This can't be undone.`}
      />
    </div>
  );
};

export default MenusManager;
